import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { AlimentoPersonaleCounter, Categoria, AlimentiFiltri, ApiResponse } from '../../../types/alimenti';
import Accordion from '../../controls/Accordion';
import PaginationControls from '../../controls/PaginationControls';
import SortIcon from '../../controls/SortIcon';
import useAccordion from '../../../hooks/useAccordion';
import usePagination from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import './AlimentiPersonali.css';


const AlimentiPersonali: React.FC = () => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState<AlimentoPersonaleCounter[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Hook per ordinamento
  const { sortedData, sortConfig, handleSort } = useSort(risultati, {
    initialField: 'nomeCompleto',
    initialDirection: 'asc'
  });
  
  // Hook per paginazione
  const {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage, 
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  } = usePagination(sortedData, { initialItemsPerPage: 10 });
  
  // Filtri
  const [filtri, setFiltri] = useState<AlimentiFiltri>({
    dataDa: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 giorni fa
    dataA: new Date().toISOString().split('T')[0], // oggi
    tipo: 'tutti',
    idCategoria: null
  });
  
  // Hook per accordion
  const filtersAccordion = useAccordion(true);
  const statsAccordion = useAccordion(true);

  // Carica categorie al mount
  useEffect(() => {
    caricaCategorie();
  }, []);

  const caricaCategorie = async () => {
    try {
      const response = await api.get<ApiResponse<Categoria[]>>('/AlimentiPersonali/categorie');
      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const cercaAlimenti = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA,
        tipo: filtri.tipo,
        idCategoria: filtri.idCategoria || undefined
      };

      const response = await api.post<ApiResponse<AlimentoPersonaleCounter[]>>('/AlimentiPersonali/conteggio', payload);
      
      if (response.data.success) {
        setRisultati(response.data.data);
        resetToFirstPage(); // Reset alla prima pagina per nuova ricerca
      } else {
        setError(response.data.message || 'Errore nella ricerca');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante la ricerca');
      console.error('Errore ricerca alimenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo: keyof AlimentiFiltri, valore: any) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };


  // Calcoli statistiche
  const totaleAlimenti = risultati.reduce((sum, item) => sum + item.numero, 0);
  const mediaAlimenti = risultati.length > 0 ? (totaleAlimenti / risultati.length).toFixed(1) : '0';

  return (
    <div className="alimenti-personali-container">
      <div className="page-header">
        <div className="header-top">
          <button 
            className="btn-back" 
            onClick={() => navigate('/')}
            title="Torna alla Dashboard"
          >
            ← Dashboard
          </button>
        </div>
        <div className="header-content-main">
          <div className="header-text">
            <h1>Alimenti Personali</h1>
            <p className="page-description">
              Visualizza e analizza gli alimenti inseriti dagli utenti del sistema
            </p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-personali')}
              disabled={true}
              title="Sei già nella vista Conteggio"
            >
              📊 Conteggio
            </button>
            
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-personali/dettaglio')}
              title="Vai alla vista dettaglio con informazioni nutrizionali complete"
            >
              🔍 Dettaglio
            </button>
          </div>
        </div>
      </div>

      {/* Sezione Filtri */}
      <div className="filtri-section">
        <Accordion 
          title="Filtri di Ricerca"
          isOpen={filtersAccordion.isOpen}
          onToggle={filtersAccordion.toggle}
        >
          <div className="filtri-grid">
            <div className="filtro-item">
              <label htmlFor="dataDa">Data Da:</label>
              <input
                type="date"
                id="dataDa"
                value={filtri.dataDa}
                onChange={(e) => handleFiltroChange('dataDa', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="dataA">Data A:</label>
              <input
                type="date"
                id="dataA"
                value={filtri.dataA}
                onChange={(e) => handleFiltroChange('dataA', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="categoria">Categoria:</label>
              <select
                id="categoria"
                value={filtri.idCategoria || ''}
                onChange={(e) => handleFiltroChange('idCategoria', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Tutte le categorie</option>
                {categorie.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.descrizione}</option>
                ))}
              </select>
            </div>

            <div className="filtro-item">
              <label htmlFor="tipo">Tipo:</label>
              <select
                id="tipo"
                value={filtri.tipo}
                onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              >
                <option value="tutti">Tutti</option>
                <option value="piatti">Piatti</option>
                <option value="alimenti">Alimenti</option>
              </select>
            </div>
          </div>

          <div className="filtri-actions">
            <button 
              className="btn-primary" 
              onClick={cercaAlimenti}
              disabled={loading}
            >
              {loading ? 'Ricerca in corso...' : 'Cerca'}
            </button>
          </div>
        </Accordion>
      </div>

      {/* Statistiche Rapide */}
      {risultati.length > 0 && (
        <div className="statistiche-section">
          <Accordion 
            title="Statistiche"
            isOpen={statsAccordion.isOpen}
            onToggle={statsAccordion.toggle}
          >
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{risultati.length}</div>
                <div className="stat-label">Utenti Attivi</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totaleAlimenti}</div>
                <div className="stat-label">Totale Alimenti</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{mediaAlimenti}</div>
                <div className="stat-label">Media per Utente</div>
              </div>
            </div>
          </Accordion>
        </div>
      )}

      {/* Messaggio di errore */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Tabella Risultati */}
      <div className="risultati-section">
        <h2>Risultati</h2>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Caricamento dati...</p>
          </div>
        )}

        {!loading && risultati.length === 0 && !error && (
          <div className="no-results">
            <p>Nessun risultato trovato per i filtri selezionati.</p>
            <p>Prova a modificare i criteri di ricerca.</p>
          </div>
        )}

        {!loading && risultati.length > 0 && (
          <>
            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showBottomControls={false}
            />

            <div className="table-container">
              <table className="risultati-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('nomeCompleto')}
                      title="Clicca per ordinare"
                    >
                      Nome Completo <SortIcon field="nomeCompleto" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('username')}
                      title="Clicca per ordinare"
                    >
                      Username <SortIcon field="username" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('email')}
                      title="Clicca per ordinare"
                    >
                      Email <SortIcon field="email" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('numero')}
                      title="Clicca per ordinare"
                    >
                      N° Alimenti <SortIcon field="numero" sortConfig={sortConfig} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={`${item.idUser}-${index}`}>
                      <td className="nome-completo">
                        {item.nomeCompleto}
                      </td>
                      <td className="username">
                        {item.username}
                      </td>
                      <td className="email">
                        {item.email}
                      </td>
                      <td className="numero-alimenti">
                        <span className="badge-numero">{item.numero}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showTopControls={false}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AlimentiPersonali;